import { Flex, Form } from 'antd'
import { sum } from 'lodash-es'
import { FunctionComponent } from 'react'
import styled from 'styled-components'
import { useAddQuotationMutation } from '@/api/workspace-api/quotation-api'
import { BasicModal } from '@/components/common/antd/basic-modal'
import { MAXIMUM_COLLABORATIVE_ITEM_COUNT } from '@/components/common/collaborative/collaborative-item-form-list'
import QuotationForm from '@/components/kol/common/kol-cooperation-detail-drawer/quotation/quotation-form'
import useNotification from '@/hooks/antd/use-notification'
import useWorkspaceCurrency from '@/hooks/use-workspace-currency'
import { useIntl } from '@/i18n/hooks/use-intl'
import {
  draftQuotationSchema,
  type DraftQuotation,
} from '@/types/schema/directory/quotation'
import type { KolSchemaModel } from '@/types/schema/kol-schema'
import { ampli } from '@/utils/ampli'
import logger from '@/utils/logger'

interface AddQuotationModalProps {
  open: boolean
  onClose: VoidFunction
  onCreated: VoidFunction
  kolInfo: KolSchemaModel
}

const AddQuotationModal: FunctionComponent<AddQuotationModalProps> = ({
  kolInfo,
  open,
  onClose,
  onCreated,
}) => {
  const { formatMessage, formatNumber } = useIntl()
  const [form] = Form.useForm<DraftQuotation>()
  const notification = useNotification()

  const [addQuotation, { isLoading }] = useAddQuotationMutation()
  const kolQuotationCurrency = useWorkspaceCurrency()

  const itemListValue = Form.useWatch(
    draftQuotationSchema.keyof().enum.item_list,
    form,
  )
  const kolId = kolInfo.id

  const handleQuotationFormFinish = async (
    values: DraftQuotation,
  ): Promise<void> => {
    try {
      await addQuotation({ kolId, quotation: values }).unwrap()

      ampli.addInquiryRecord({
        kolId,
        kolName: kolInfo.name,
        inquiryName: values.name,
        platforms: values.item_list.map((item) => item.platform_name),
        postTypes: values.item_list.map((item) => item.collaborative_item),
        counts: values.item_list.map((item) => item.collaborative_items_count),
        costs: values.item_list.map((item) => item.cooperation_fee),
        currency: kolQuotationCurrency,
        value: sum(values.item_list.map((item) => item.cooperation_fee)),
        count: values.item_list.length,
      })

      onClose()
      onCreated()
    } catch (error) {
      logger.error(error)

      notification.error({
        message: formatMessage({
          id: 'general:operation_failure',
        }),
        placement: 'bottomRight',
      })
    }
  }

  return (
    <BasicModal
      centered
      destroyOnClose
      footer={(_, { OkBtn }) => {
        return (
          <Flex align='center' gap={16} justify='end'>
            <QuotationItemCount>
              {formatMessage(
                {
                  id: 'kol_cooperation_detail_drawer:collaborative_item_count',
                },
                {
                  collaborativeItemCount: formatNumber(itemListValue?.length),
                  maximumCollaborativeItemCount: formatNumber(
                    MAXIMUM_COLLABORATIVE_ITEM_COUNT,
                  ),
                },
              )}
            </QuotationItemCount>
            <OkBtn />
          </Flex>
        )
      }}
      okButtonProps={{ type: 'primary', loading: isLoading }}
      okText={formatMessage({ id: 'general:submit_btn' })}
      open={open}
      title={formatMessage({
        id: 'footprint:collaboration_detail_quotation_new',
      })}
      width={900}
      onCancel={onClose}
      onOk={form.submit}
    >
      <Hint>
        {formatMessage({
          id: 'kol_cooperation_detail_drawer:add_quotation_modal_hint',
        })}
      </Hint>
      <QuotationForm
        disabled={isLoading}
        form={form}
        onFinish={handleQuotationFormFinish}
      />
    </BasicModal>
  )
}

const QuotationItemCount = styled.div`
  color: ${({ theme }): string => theme.colors.text.tooltip};
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px;
`

const Hint = styled.div`
  color: ${({ theme }): string => theme.colors.text.secondary};
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 140%;
  margin: 0 0 12px;
`

export default AddQuotationModal
